import { Text, VStack } from '@talos/kyoko';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface AssetLeaderboardLoadingBarProps {
  assetCount: number;
  loadedAssetCount: number;
}

const PROGRESS_CREEP_CAP = 92;
const PROGRESS_CREEP_TAU_MS = 1800;
const PROGRESS_TICK_MS = 100;

export function advanceLoadingProgress(current: number, realPercent: number, dtMs: number): number {
  if (realPercent >= 100) {
    return 100;
  }
  const eased = current + (PROGRESS_CREEP_CAP - current) * (1 - Math.exp(-dtMs / PROGRESS_CREEP_TAU_MS));
  return Math.max(eased, realPercent);
}

export function AssetLeaderboardLoadingBar({ assetCount, loadedAssetCount }: AssetLeaderboardLoadingBarProps) {
  const isResolvingUniverse = assetCount === 0;
  const realPercent = isResolvingUniverse ? 0 : (Math.min(loadedAssetCount, assetCount) / assetCount) * 100;

  // Read the latest real fraction from inside the interval without resubscribing it every tick.
  const realPercentRef = useRef(realPercent);
  realPercentRef.current = realPercent;

  const [displayedPercent, setDisplayedPercent] = useState(0);
  useEffect(() => {
    let last = performance.now();
    const intervalId = setInterval(() => {
      const now = performance.now();
      const dtMs = now - last;
      last = now;
      setDisplayedPercent(prev => advanceLoadingProgress(prev, realPercentRef.current, dtMs));
    }, PROGRESS_TICK_MS);
    return () => clearInterval(intervalId);
  }, []);

  const label = isResolvingUniverse
    ? 'Resolving asset universe…'
    : `Loading ${assetCount.toLocaleString()} assets… ${Math.round(displayedPercent)}%`;

  return (
    <VStack flex="1" w="100%" h="100%" justifyContent="center" alignItems="center" gap="spacing12">
      <Track
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        // Omitted while resolving the universe so assistive tech reports an indeterminate bar.
        aria-valuenow={isResolvingUniverse ? undefined : Math.round(displayedPercent)}
      >
        <Fill style={{ width: `${displayedPercent}%` }} />
      </Track>
      <Text color="colorTextSubtle">{label}</Text>
    </VStack>
  );
}

const Track = styled.div`
  width: 240px;
  height: 4px;
  overflow: hidden;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.gray['030']};
`;

const Fill = styled.div`
  height: 100%;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.primary.lighten};
  transition: width ${PROGRESS_TICK_MS + 50}ms linear;
`;
